import { useEffect, type JSX } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { endSessionNow, isAgentArea } from './auth/sessionLifecycle.js'
import { ConfirmDialog } from 'primereact/confirmdialog'
import { useAuthStore } from './store/authStore.js'
import { ErrorBoundary } from './components/system/ErrorBoundary.js'
import { ToastProvider } from './components/system/ToastProvider.js'
import { SkipLink } from './components/system/SkipLink.js'
import { SearchPalette } from './components/system/SearchPalette.js'
import { useSessionValidation } from './hooks/useSessionValidation.js'

function AppShell(): JSX.Element {
  const user = useAuthStore((s) => s.user)
  const location = useLocation()

  // Global session validation: startup check + heartbeat + tab-close cleanup
  useSessionValidation()

  // An admin-login session never enters the agent portal (requireAuth covers
  // full page loads; this covers in-app navigation, where loaders don't re-run).
  const navigate = useNavigate()
  const adminInAgentPortal = user?.portal === 'admin' && isAgentArea(location.pathname)
  useEffect(() => {
    if (!adminInAgentPortal) return
    void endSessionNow(false).then(() => navigate('/login', { replace: true }))
  }, [adminInAgentPortal, navigate])

  return (
    <div className="min-h-screen bg-gray-50">
      <SkipLink />

      <main id="main-content" className="p-0">
        <Outlet />
      </main>
    </div>
  )
}

export default function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <ToastProvider>
        {/* Single global mount so the imperative confirmDialog({...}) registry
            works app-wide (used by hard-delete admin flows — see
            components/admin/README.md). */}
        <ConfirmDialog />
        {/* Global Cmd-K palette — opens on Cmd-K, Ctrl-K, or the
            'open-search' CustomEvent dispatched by the layout buttons. */}
        <SearchPalette />
        <AppShell />
      </ToastProvider>
    </ErrorBoundary>
  )
}
